import { Fragment, SyntheticEvent, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { observer } from 'mobx-react-lite';
import { useFormik } from 'formik';
import { Alert, Button, FormHelperText } from '@mui/material';
import Grid from '@mui/material/Grid2';

import { isArray, useToggle } from '@cutwise/utils';

import { URLService } from '@/dmdrive/core/service/URLService';
import Upload3DModelButton from '@/dmdrive/drive/components/Upload3DModelButton/Upload3DModelButton';
import { DriveModel } from '@/dmdrive/drive/entities/DriveModel';
import { CreateProjectValues } from '@/dmdrive/drive/types/ProjectValues';
import { useStore } from '@/dmdrive/store';
import { ContentModal, InputField } from '@/dmdrive/ui-kit/components';

import AttachedModelRow from './AttachedModelRow/AttachedModelRow';

interface CreateProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  files?: File[];
}

function CreateProjectModal(props: CreateProjectModalProps) {
  const { isOpen, onClose, files } = props;
  const { isUploadingModel, createProject } = useStore('driveStore');
  const navigate = useNavigate();

  const [shouldShowLimitError, showLimitError, hideLimitError] = useToggle();

  const { values, errors, handleChange, setFieldValue, handleSubmit, submitForm, resetForm } =
    useFormik<CreateProjectValues>({
      validateOnMount: false,
      validateOnBlur: false,
      validateOnChange: false,
      initialValues: {
        title: '',
        plan: null,
        result: null,
      },
      onSubmit: async (values: CreateProjectValues) => {
        const conference = (await createProject(values)) as unknown as DriveModel;

        onClose();

        return conference;
      },
      validate(values: CreateProjectValues) {
        const { plan, result } = values;
        const errors: Partial<Record<keyof CreateProjectValues, string>> = {};

        if (!plan || !result) {
          errors.plan = 'Attach two files for Plan and Result';
        }

        return errors;
      },
    });

  const applyFiles = useCallback(
    (files: Nullable<File[]>) => {
      if (isArray(files)) {
        if (files!.length === 1) {
          setFieldValue(!values.plan ? 'plan' : 'result', files![0]);
        } else if (files!.length > 1) {
          setFieldValue('plan', files![0]);
          setFieldValue('result', files![1]);

          if (files!.length > 2) {
            showLimitError();
          }
        }
      }
    },
    [values.plan],
  );

  const onDeletePlan = useCallback(() => {
    if (values.result) {
      setFieldValue('plan', values.result);
      setFieldValue('result', null);
    } else {
      setFieldValue('plan', null);
    }
  }, [values.result]);

  const onDeleteResult = useCallback(() => setFieldValue('result', null), []);

  const onCreateAndOpen = useCallback(
    async (e: SyntheticEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const conference = await submitForm();

      if (conference) {
        navigate(URLService.topic(conference.conferenceId));
      }
    },
    [JSON.stringify(values)],
  );

  useEffect(() => {
    if (isOpen) {
      hideLimitError();

      if (isArray(files)) {
        applyFiles(files!);
      }
    } else {
      resetForm({
        values: {
          title: '',
          plan: null,
          result: null,
        },
      });
    }
  }, [isOpen]);

  return (
    <ContentModal maxWidth="xs" onClose={onClose} open={isOpen} isLoading={isUploadingModel}>
      <form onSubmit={handleSubmit} onReset={onClose}>
        <ContentModal.Header onClose={onClose}>Create Project</ContentModal.Header>

        <ContentModal.Body>
          {shouldShowLimitError && <Alert color="error">You can upload only 2 models at once!</Alert>}

          <Grid container spacing={2}>
            {values.plan && (
              <Grid size={12}>
                <AttachedModelRow heading="Plan" title={values.plan.name} onDelete={onDeletePlan} />

                {values.result && (
                  <AttachedModelRow heading="Result" title={values.result.name} onDelete={onDeleteResult} />
                )}
              </Grid>
            )}

            {(!values.plan || !values.result) && (
              <Fragment>
                <Grid size={12}>
                  <div>
                    <Upload3DModelButton onSelectFile={applyFiles} />
                  </div>

                  {errors.plan && <FormHelperText error>{errors.plan}</FormHelperText>}
                </Grid>
              </Fragment>
            )}

            <Grid size={12}>
              <InputField
                label="Project name (optional)"
                name="title"
                value={values.title}
                onChange={handleChange}
                error={!!errors.title}
                helperText={errors.title}
                isFullWidth
              />
            </Grid>
          </Grid>
        </ContentModal.Body>

        <ContentModal.Footer>
          <Button type="reset">Cancel</Button>

          <Button type="submit" variant="contained">
            Create
          </Button>

          <Button type="button" variant="contained" onClick={onCreateAndOpen}>
            Create & open
          </Button>
        </ContentModal.Footer>
      </form>
    </ContentModal>
  );
}

export default observer(CreateProjectModal);
